import { memo } from "react";
import { Pressable, Text, View } from "react-native";
import { Image } from "expo-image";
import { Link } from "expo-router";
import type { Event } from "@lezz/shared";
import { resolveStorageUrl } from "@lezz/supabase";
import { formatShortEventDate } from "@/shared/lib/format-date";
import type { ParticipantPreview } from "@/features/events/lib/attach-participants-preview";
import { DistanceChip } from "@/shared/components/ui/distance-chip";
import { EventCardDefault } from "./event-card-default";

type EventCardProps = {
  event: Event & {
    participantsPreview?: ParticipantPreview[];
    participantCount?: number;
  };
  variant?: "default" | "compact" | "carousel";
};

const EventCardCompact = memo(function EventCardCompact({
  event,
}: Omit<EventCardProps, "variant">) {
  return (
    <Link asChild href={`/event/${event.eventId}`}>
      <Pressable
        className="bg-card-event border-border-card-event flex-row overflow-hidden rounded-xl border active:opacity-70"
        accessibilityRole="button"
        accessibilityLabel={`View details for ${event.title}`}
      >
        {event.imageUrl ? (
          <Image
            source={{ uri: resolveStorageUrl(event.imageUrl) }}
            className="self-stretch rounded-l-xl"
            contentFit="cover"
            style={{ width: 110 }}
            accessibilityLabel={`Event image for ${event.title}`}
          />
        ) : (
          <View
            className="bg-brand self-stretch rounded-l-xl"
            style={{ width: 110 }}
          />
        )}
        <View className="flex-1 justify-center p-4">
          <Text
            className="font-display text-foreground text-base font-semibold"
            numberOfLines={1}
          >
            {event.title}
          </Text>
          <View className="mt-1 flex-row items-center gap-2 text-sm">
            <Text className="text-event-date">
              {formatShortEventDate(event.dateAndTime)}
            </Text>
            {event.hostName && (
              <Text className="text-muted-foreground" numberOfLines={1}>
                {" • "} {event.hostName}
              </Text>
            )}
            <DistanceChip lat={event.lat} lng={event.lng} />
          </View>
        </View>
      </Pressable>
    </Link>
  );
});

const EventCardCarousel = memo(function EventCardCarousel({
  event,
}: Omit<EventCardProps, "variant">) {
  return (
    <Link asChild href={`/event/${event.eventId}`}>
      <Pressable
        className="bg-card-event overflow-hidden rounded-xl active:opacity-70"
        style={{ width: 190 }}
        accessibilityRole="button"
        accessibilityLabel={`View details for ${event.title}`}
      >
        <View className="relative" style={{ height: 140 }}>
          {event.imageUrl ? (
            <Image
              source={{ uri: resolveStorageUrl(event.imageUrl) }}
              className="h-full w-full"
              contentFit="cover"
              style={{ width: 190, height: 140 }}
              accessibilityLabel={`Event image for ${event.title}`}
            />
          ) : (
            <View className="bg-brand h-full w-full" />
          )}
        </View>

        <View className="bg-card-event px-3 pt-2 pb-3">
          <Text
            className="font-display text-foreground text-sm font-semibold"
            numberOfLines={1}
          >
            {event.title}
          </Text>
          <View className="mt-2 flex-row items-center gap-2">
            <Text className="text-event-date text-xs">
              {formatShortEventDate(event.dateAndTime)}
            </Text>
            {event.hostName && (
              <>
                <Text className="text-muted-foreground text-xs">•</Text>
                <Text
                  className="text-muted-foreground text-xs"
                  numberOfLines={1}
                >
                  {event.hostName}
                </Text>
              </>
            )}
          </View>
          <DistanceChip lat={event.lat} lng={event.lng} className="mt-2" />
        </View>
      </Pressable>
    </Link>
  );
});

export function EventCard({ variant = "default", ...props }: EventCardProps) {
  if (variant === "compact") {
    return <EventCardCompact {...props} />;
  }

  if (variant === "carousel") {
    return <EventCardCarousel {...props} />;
  }

  return <EventCardDefault {...props} />;
}
